import type { McpProgressEvent } from "types";
import styles from "./ProgressEvent.module.scss";

interface IndeterminateProgressEventProps {
  message: McpProgressEvent["message"];
  className?: string;
}

function IndeterminateProgressEvent({
  message,
  className,
}: IndeterminateProgressEventProps) {
  return (
    <div className={`${styles.mcpProgressContainer} ${className}`}>
      {message && (
        <div className={`${styles.mcpProgressMessage} f6 gray`}>
          {"● " + message}
        </div>
      )}
      <div className={styles.mcpProgressBarContainer}>
        <div className={styles.mcpProgressBarTrack}>
          <div
            className={`${styles.mcpProgressBarFill}`}
            style={{ width: `1%` }}
          />
        </div>
      </div>
    </div>
  );
}

interface PercentageProgressEventProps {
  progress: number;
  total: number;
  message: McpProgressEvent["message"];
  className?: string;
}

function PercentageProgressEvent({
  progress,
  total,
  message,
  className,
}: PercentageProgressEventProps) {
  // Calculate normalized progress (0.0 to 1.0) with safe division handling
  const normalizedProgress = total > 0 ? Math.min(progress / total, 1.0) : 0;
  const isComplete = normalizedProgress >= 1;
  const percentage = Math.round(normalizedProgress * 100);

  // Always display as x/y format since we always have a dynamic total
  const progressDisplay = `${progress}/${total}`;

  // Do not display completed events.
  if (isComplete) return null;

  return (
    <div className={`${styles.mcpProgressContainer} ${className}`}>
      {message && (
        <div className={`${styles.mcpProgressMessage} f6 gray`}>
          {isComplete ? "✓" : "●"} {message}
        </div>
      )}
      <div className={styles.mcpProgressBarContainer}>
        <div className={styles.mcpProgressBarTrack}>
          <div
            className={`${styles.mcpProgressBarFill}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className={`${styles.mcpProgressPercentage} f7 gray ml2`}>
          {progressDisplay}
        </div>
      </div>
    </div>
  );
}

interface ProgressEventProps {
  event: McpProgressEvent;
  className?: string;
}

export default function ProgressEvent({
  event,
  className = "",
}: ProgressEventProps) {
  const progress = event.progress;
  const total = event.total;
  const message = event.message;

  if (typeof total === "number") {
    return (
      <PercentageProgressEvent
        progress={progress}
        total={total}
        message={message}
        className={className}
      />
    );
  }

  return <IndeterminateProgressEvent message={message} className={className} />;
}
